import { type JSX, useCallback, useEffect, useState } from "react";
import { type ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { GenericError } from "@via/components/error.tsx";
import TableContextMenu from "@via/components/table/table-context-menu.tsx";
import Table, { DrawerSize } from "@via/components/table/table.tsx";
import useUrlParams from "@via/hooks/useURLParams.tsx";
import {
  useDeleteModelSkill,
  useModelSkillsList,
} from "@via/pages/cortex/model-skill/model-skill.api.tsx";
import { EMPTY_TABLE_MESSAGE, ERROR, ROUTES } from "@via/utils/constants.tsx";
import { classNames, getElapsedTime } from "@via/utils/helpers.tsx";
import type { ModelSkillListItem } from "common";
import DeleteModal from "@via/components/delete-modal/delete-modal.tsx";
import { ActionButton } from "@via/components/action-button/action-button.tsx";
import Drawer from "@via/components/drawer.tsx";
import CreateModelSkillForm from "@via/pages/cortex/model-skill/create-model-skill-form.tsx";
import EditModelSkillForm from "@via/pages/cortex/model-skill/edit-model-skill-form.tsx";
import { useRegisterCortexContext } from "@via/hooks/useRegisterCortexContext";
import { useCortexContext } from "@via/context/cortex-context";

const defaultParams = {
  sort: [{ id: "createdAt", desc: true }],
  filters: [],
  page: 0,
  pageSize: 10,
};

const MODEL_SKILL_TABLE_OPTIONS = [
  { name: "Edit", href: "#" },
  { name: "Delete", href: "#" },
];

const ModelSkill = (): JSX.Element => {
  const { setUrlParams, getInitialParams } = useUrlParams(defaultParams);
  const initialParams = getInitialParams();

  const [page, setPage] = useState(initialParams.page);
  const [pageSize, setPageSize] = useState(initialParams.pageSize);
  const [sort, setSort] = useState<Array<{ id: string; desc: boolean }>>(
    initialParams.sort
  );
  const [columnFilters] = useState<Array<{ id: string; value: unknown }>>(
    initialParams.filters
  );
  const [modelSkillToBeDeleted, setModelSkillToBeDeleted] =
    useState<ModelSkillListItem | null>(null);
  const [isCreateDrawerOpen, setIsCreateDrawerOpen] = useState(false);
  const [isEditDrawerOpen, setIsEditDrawerOpen] = useState(false);
  const [modelSkillToBeEdited, setModelSkillToBeEdited] =
    useState<ModelSkillListItem | null>(null);

  const {
    isDrawerOpen: isCortexDrawerOpen,
    setShouldUsePortalForCortexDrawer,
  } = useCortexContext();

  // While Model Skill create/edit drawers are open (blocking overlays),
  // force Cortex into portal/overlay mode so it can be opened on top.
  useEffect(() => {
    const wantsPortal = isCreateDrawerOpen || isEditDrawerOpen;
    if (wantsPortal) {
      setShouldUsePortalForCortexDrawer(true);
      return;
    }
    if (!isCortexDrawerOpen) setShouldUsePortalForCortexDrawer(false);
  }, [
    isCreateDrawerOpen,
    isEditDrawerOpen,
    isCortexDrawerOpen,
    setShouldUsePortalForCortexDrawer,
  ]);

  useEffect(() => {
    return () => {
      setShouldUsePortalForCortexDrawer(false);
    };
  }, [setShouldUsePortalForCortexDrawer]);

  const {
    data: modelSkillsList,
    isError: isModelSkillsListError,
    isLoading: isModelSkillsListLoading,
  } = useModelSkillsList(page, pageSize, sort, columnFilters);

  const {
    mutateAsync: deleteModelSkill,
    isPending: isDeleteModelSkillLoading,
  } = useDeleteModelSkill();

  const handleContextMenuAction = (
    name: string,
    modelSkill: ModelSkillListItem
  ): void => {
    switch (name) {
      case "Edit":
        setModelSkillToBeEdited(modelSkill);
        setIsEditDrawerOpen(true);
        break;
      case "Delete":
        setModelSkillToBeDeleted(modelSkill);
        break;
    }
  };

  useEffect(() => {
    setUrlParams({ sort, filters: columnFilters, page, pageSize });
  }, [sort, columnFilters, page, pageSize]);

  const getModelSkillsContext = useCallback(() => {
    const modelSkills = modelSkillsList?.data || [];
    const total = modelSkillsList?.total || 0;

    return {
      modelSkills: modelSkills.map(modelSkill => ({
        id: modelSkill.id,
        name: modelSkill.name,
        ownerUsername: modelSkill.ownerUsername,
        createdAt: modelSkill.createdAt,
        updatedAt: modelSkill.updatedAt,
      })),
      total: total,
      currentPage: page,
      pageSize: pageSize,
      filters: columnFilters,
      sortBy: sort.length > 0 ? sort[0] : null,
    };
  }, [modelSkillsList, page, pageSize, sort, columnFilters]);

  useRegisterCortexContext({
    routePattern: ROUTES.CORTEX_MODEL_SKILL,
    contextName: "Model Skill",
    getContext: getModelSkillsContext,
    systemPrompt:
      "You are analyzing model skills list page. Help answer questions about model skills, help find specific model skills, and assist with filtering and sorting.",
  });

  const columnHelper = createColumnHelper<ModelSkillListItem>();
  const columns = [
    columnHelper.accessor("id", {
      cell: ({ row }) => {
        return (
          <button
            className="via-link truncate"
            onClick={e => {
              e.preventDefault();
              handleContextMenuAction("Edit", row.original);
            }}>
            {row.original.id}
          </button>
        );
      },
      header: () => <span>ID</span>,
      size: 100,
    }),
    columnHelper.accessor("name", {
      cell: ({ row }) => {
        return (
          <button
            className={classNames("via-link", "truncate")}
            onClick={e => {
              e.preventDefault();
              handleContextMenuAction("Edit", row.original);
            }}>
            {row.original.name}
          </button>
        );
      },
      header: () => <span>Name</span>,
      size: 120,
    }),
    columnHelper.accessor("ownerUsername", {
      cell: info => {
        return <span>{info.getValue()}</span>;
      },
      header: () => "Owner",
      size: 120,
      enableSorting: false,
    }),
    columnHelper.accessor("createdAt", {
      cell: info => {
        const value = info.getValue();
        return <span>{getElapsedTime(value.toString())}</span>;
      },
      header: () => <span>Date Created</span>,
      size: 130,
    }),
    columnHelper.accessor("updatedAt", {
      cell: info => {
        const value = info.getValue();
        return <span>{getElapsedTime(value.toString())}</span>;
      },
      header: () => <span>Last Modified</span>,
      size: 130,
    }),
    columnHelper.accessor("id", {
      cell: ({ row }) => {
        const contextMenuOptions = MODEL_SKILL_TABLE_OPTIONS;

        return (
          <TableContextMenu
            options={contextMenuOptions}
            onSelect={option => {
              handleContextMenuAction(option.name, row.original);
            }}
          />
        );
      },
      header: () => "Options",
      size: 70,
      enableSorting: false,
    }),
  ] as Array<ColumnDef<ModelSkillListItem>>;

  return (
    <main className="py-4">
      <div className="via-page-heading-wrapper">
        <h2 className="via-h2">Model Skills</h2>
        <ActionButton
          button="Create Model Skill"
          primary={true}
          onClick={() => {
            setIsCreateDrawerOpen(true);
          }}
        />
      </div>

      <Drawer
        id="cortex-create-model-skill"
        open={isCreateDrawerOpen}
        setOpen={setIsCreateDrawerOpen}
        size={DrawerSize.Big}>
        <CreateModelSkillForm setOpen={setIsCreateDrawerOpen} />
      </Drawer>

      {modelSkillToBeEdited && (
        <Drawer
          id="cortex-edit-model-skill"
          open={isEditDrawerOpen}
          setOpen={open => {
            setIsEditDrawerOpen(open);
            if (!open) {
              setModelSkillToBeEdited(null);
            }
          }}
          size={DrawerSize.Big}>
          <EditModelSkillForm
            modelSkill={modelSkillToBeEdited}
            setOpen={open => {
              setIsEditDrawerOpen(open);
              if (!open) {
                setModelSkillToBeEdited(null);
              }
            }}
          />
        </Drawer>
      )}

      <DeleteModal
        item={modelSkillToBeDeleted}
        setItem={setModelSkillToBeDeleted}
        deleteFunction={() => deleteModelSkill(modelSkillToBeDeleted!.id)}
        isLoading={isDeleteModelSkillLoading}
        title="Delete Model Skill"
      />

      {isModelSkillsListError ? (
        <div className="via-generic-error">
          <GenericError message={ERROR} />
        </div>
      ) : (
        <Table
          isLoading={isModelSkillsListLoading}
          data={modelSkillsList?.data ?? []}
          columns={columns}
          sort={sort}
          setSort={setSort}
          page={page}
          setPage={setPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          total={modelSkillsList?.total}
          emptyMessage={EMPTY_TABLE_MESSAGE.DEFAULT}
          applyColumnSize={true}
        />
      )}
    </main>
  );
};

export default ModelSkill;
